//
// AddApptCard
//
// This card is used by the VehLayout screen and allows the user
// to start an appointment add for the vehicle being displayed.

import React, { useCallback, useEffect, useState } from 'react';
import { IView } from 'Framework';
import { LoadingProgress } from '@framework-controls/core';
import { strings } from 'src/localization';
import { ViewMode } from 'src/enums';
import { ComponentMapIds } from '../map';
import { IServicesState } from 'src/store/states';
import { SpecData, ascCtaInteractionInput } from 'src/models';
import { VehData } from 'src/models/WebOp/WebOp';
import { VehicleRecs } from 'src/models/Vehicles/Vehicles';
import { IAppointmentState } from 'src/store/states/Appointment/AppointmentSlice';
import { UpdateApptVehInp } from 'src/models/Appointment/Appointment';
import SVPButton from '../SVPButtons/SVPButton';
import css from './AddApptCard.module.scss';
import { ascCtaInteraction } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';

export interface IProps {
    viewMode: ViewMode;
    appointment: IAppointmentState;
    vehicles: VehicleRecs;
    selectedVehicle: string;
    specProfile: string;
    store: string;
    branch: string;
    specData?: SpecData;
    additionalServices: IServicesState;
    apptVin?: string;
    inquiryInvoked: boolean;
    showWeOwes?: boolean;
    showOEMRecall?: boolean;
    showPrvRecom?: boolean;
    menusReturnCode: string;
    theme: string;
    locale: string;
    myGMRewardsLaunched: string | undefined;
    gmMemberErr: string | undefined;
    clearMainPanes: () => void;
    pushToMainPanes: (inputView: IView) => void;
    setViewMode: (viewMode: ViewMode) => void;
    getMenus: (value1, value2) => void;
    getAdditionalServices: (profileNum: string, store: string, branch: string) => void;
    getApptOps: (profileNum: string, store: string, branch: string, vehData?: VehData,) => void;
    updateApptVehicle: (inp: UpdateApptVehInp) => void;
    clearAdditionalServices: () => void;
    clearMenusState: () => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    gmMemberSearch: (inp: string) => void;
    setApptHistId: (id: string) => void;
}

const AddApptCard = (props: IProps) => {
    const [gettingData, setGettingData] = useState(false);
    const [clearingData, setClearingData] = useState(false);
    const noArrow = props.theme === 'porsche' || props.theme === 'bmw' ? true : false;

    // Determine which header message to display for this card
    // If the vehicle has no existing appointments then use ms1
    // If the vehicle has existing appointments then use ms2
    let cardMessage = strings.AddApptMsg1;
    if (props.appointment.ViewApptList) {
        const apptVeh = props.appointment.ViewApptList.findIndex(veh => veh.vin === props.appointment.ApptVeh.vin);
        if (apptVeh !== -1) {
            if (props.appointment.ViewApptList[apptVeh].apptNum !== '') {
                cardMessage = strings.AddApptMsg2;
            }
        }
    }

    // Button handler for the New Appt button.
    // Get the record data needed and go to the AdditoinalServices screen
    const btnHandler = useCallback((e: any) => {
        const vehicleIndex = parseInt(props.selectedVehicle, 10);
        const selVeh = props.vehicles[vehicleIndex];
        const mileage = selVeh.prevMileage;
        const inp: UpdateApptVehInp = {
            vin: selVeh.vin,
            makeCode: selVeh.makeCode,
            make: selVeh.make,
            model: selVeh.model,
            series: selVeh.style,
            vehYr: selVeh.year,
            vehColor: selVeh.color,
            mileage: mileage,
            imagePath: selVeh.imagePath,
        };


        props.updateApptVehicle(inp);
        props.setApptHistId(props.selectedVehicle);
        props.clearMenusState();
        setClearingData(true);
        if (props.myGMRewardsLaunched === 'Y' && !props.gmMemberErr) {
            props.gmMemberSearch(props.appointment.ApptCust.custEmailAddress);
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.NewAppt,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props?.vehicles, props?.updateApptVehicle, props.appointment.ApptCust.custEmailAddress, props.myGMRewardsLaunched,
        props?.selectedVehicle, props.clearMenusState, props.gmMemberSearch, props.gmMemberErr, props.setApptHistId]);

    useEffect(() => {
        // Clear services and update state before attempting to rebuild services
        if (clearingData && props.menusReturnCode === '') {
            props.getApptOps(props.specProfile, props.store, props.branch);
            props.getMenus('', '');
            props.setViewMode(ViewMode.Appt);
            setClearingData(false);
            setGettingData(true);
            // log Appointment Add Process Start action
            props.addLogEntry('Appointment Add Process Start', '', '');
        }
    }, [props.getAdditionalServices, props.getApptOps, props.getMenus, props.specProfile, clearingData,
        props.store, props.branch, props.additionalServices.allServices.built, props.menusReturnCode,
        props.setViewMode, props.addLogEntry]);

    // once we have the data needed switch to the correct starting screen
    // to start the process of adding a new appointment for this vehicle.
    useEffect(() => {
        if (gettingData && props.menusReturnCode !== '') {
            setGettingData(false);
            // Go to the Add Skip Services screen if specs indicates that is a valid screen.
            // Other wise go to the Addtional Services screen if there is data to display there
            // Other wise go to the ServiceTab
            if (props.specData?.skipServicesFlag) {
                props.pushToMainPanes({
                    viewId: ComponentMapIds.AddSkipServices,
                    component: ComponentMapIds.AddSkipServices,
                });
            } else {
                let nextViewId = ComponentMapIds.AddVehicle;
                let nextComponent = ComponentMapIds.AddVehicle;
                if (!props.apptVin || (!props.showWeOwes && !props.showPrvRecom && (!props.showOEMRecall ||
                    !props.inquiryInvoked))) {
                    nextViewId = ComponentMapIds.ServiceTab;
                    nextComponent = ComponentMapIds.ServiceTab;
                }
                if (!props.additionalServices.allServices.weOwes[0] && !props.additionalServices.allServices.recom[0] &&
                    !props.additionalServices.allServices.inquiryInvoked) {
                    nextViewId = ComponentMapIds.AddVehicle;
                    nextComponent = ComponentMapIds.AddVehicle;
                }

                props.pushToMainPanes({
                    viewId: nextViewId,
                    component: nextComponent,
                });
            }
        }
    }, [props.specData, props.pushToMainPanes, props.apptVin, props.showOEMRecall,
        props.showPrvRecom, props.showWeOwes, props.inquiryInvoked, props.additionalServices,
        gettingData, props.menusReturnCode]);

    // speical css logic for OEMs
    const summaryTitleClass = classNames(
        css.summaryTitle,
        {
            [css.summaryTitleBuick]: props.theme === 'buick',
            [css.summaryTitleDodge]: props.theme === 'dodge',
            [css.summaryTitleIN]: props.theme === 'infiniti',
            [css.summaryTitleMazda]: props.theme === 'mazda',
            [css.summaryTitleAcura]: props.theme === 'acura',
        },
    );

    // the length of the French literal for RefreshRepair, which is shown on Vehicle Layout
    // requires a smaller font size. Since this button appears on the Vehicle Layout screen as well,
    // adjust its font size to match, if locale is fr-CA
    let longLitFlag = '';
    if (props.locale === 'fr-CA') {
        longLitFlag = 'B';
    }

    return (
        <div>
            {gettingData ? <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> : null}
            <hr className={css.hr} />
            <h4 className={summaryTitleClass}>{strings.AddNewAppt}</h4>
            <span id='cardMsg' className={props.theme==='bmw' ? css.subhead : ''}>
                {cardMessage}
            </span>
            <div className={css.container}>
                <div className={css.btnDiv}>
                    <div className={css.btnDiv2}>
                        <SVPButton
                            id='AddApptBtn'
                            variant={'secondaryBtnAlt'}
                            message={strings.NewAppt}
                            handler={btnHandler}
                            tooltip={strings.SchNewAppt}
                            longLitFlag={longLitFlag}
                            theme={props.theme}
                            noArrow={noArrow}
                        />
                    </div>
                </div>
                <hr className={css.hr} />
            </div>

        </div>
    );
};

export default AddApptCard;